import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { dispatchEvent, testReducedMotion, toArray, toggleAttribute } from '@nrk/dh-basis-common';

var STATE_ATTR = 'data-state';
var VIEWPORT_POSITION_ATTR = 'data-viewport-position';
var DISABLE_FORCED_ATTRIBUTES_ATTR = 'disable-forced-attributes';
var IS_PAUSED = 'is-paused';
var AUTOPLAY_BLOCKED = 'autoplay-blocked';
var READY_STATE = {
  HAVE_NOTHING: 0,
  HAVE_METADATA: 1,
  HAVE_CURRENT_DATA: 2,
  HAVE_FUTURE_DATA: 3,
  HAVE_ENOUGH_DATA: 4 // Can play through
};
var isVisible = child => !!child.offsetParent;
class LevendeStillbilde extends HTMLElement {
  constructor() {
    super(...arguments);
    _defineProperty(this, "_disconnecting", false);
    _defineProperty(this, "_visibleVideoElement", void 0);
    _defineProperty(this, "_handlePlayChange", e => {
      if (e.type === 'play') {
        dispatchEvent(this, 'dh-basis-autoplay-succeeded');
      }
      if (e.target === this._visibleVideoElement) {
        this.isPaused = this._visibleVideoElement.paused;
      }
    });
    _defineProperty(this, "_updatePlayState", () => {
      if (!this._visibleVideoElement) {
        return;
      }
      if (this._shouldPlay()) {
        this._playVideo();
      } else {
        this._pauseVideo();
      }
    });
    _defineProperty(this, "_handleResize", () => {
      this._updateVisibleVideoElement();
    });
  }
  static get observedAttributes() {
    return [STATE_ATTR, DISABLE_FORCED_ATTRIBUTES_ATTR, VIEWPORT_POSITION_ATTR];
  }
  connectedCallback() {
    this._setVideoAttributes();
    this._setEventListeners();
    this._updateVisibleVideoElement();
  }
  disconnectedCallback() {
    this._disconnecting = true;
    this._removeEventListeners();
  }
  attributeChangedCallback(name, prevValue, newValue) {
    var valueChanged = prevValue !== newValue;
    if (name === STATE_ATTR || name === VIEWPORT_POSITION_ATTR) {
      if (valueChanged) {
        this._updateVisibleVideoElement();
        this._updatePlayState();
        if (name === VIEWPORT_POSITION_ATTR && newValue === 'IN-VIEWPORT') {
          this._preload();
        }
      }
    }
  }
  _setEventListeners() {
    window.addEventListener('resize', this._handleResize);
    this.videoElements.forEach(el => {
      el.addEventListener('pause', this._handlePlayChange);
      el.addEventListener('play', this._handlePlayChange);
      el.addEventListener('canplaythrough', this._updatePlayState);
    });
  }
  _removeEventListeners() {
    window.removeEventListener('resize', this._handleResize);
    this.videoElements.forEach(el => {
      el.removeEventListener('pause', this._handlePlayChange);
      el.removeEventListener('play', this._handlePlayChange);
      el.removeEventListener('canplaythrough', this._updatePlayState);
    });
  }
  _updateVisibleVideoElement() {
    // eslint-disable-next-line compat/compat
    requestAnimationFrame(() => {
      var nextVisibleVideo = this.videoElements.find(isVisible);
      if (nextVisibleVideo !== this._visibleVideoElement) {
        // Pause previous video
        this._pauseVideo();
        this._visibleVideoElement = nextVisibleVideo;
        if (this._shouldPreload()) {
          this._preload();
        }
        this._updatePlayState();
      }
    });
  }
  _setVideoAttributes() {
    if (!this.disableForcedAttributes) {
      this.videoElements.forEach(el => {
        el.setAttribute('playsInline', '');
        el.setAttribute('loop', '');
        el.setAttribute('disableRemotePlayback', '');
        el.setAttribute('x-webkit-airplay', 'deny');
      });
    }
  }
  _shouldPlay() {
    var _this$_visibleVideoEl;
    /*
     * Safari 14-15.5 hides poster on play, leaving a transparent element until enough data is available to play through.
     * We have to wait for the canplaythrough event and readystate = READY_STATE.HAVE_ENOUGH_DATA before calling `.play()`
     * */
    return ((_this$_visibleVideoEl = this._visibleVideoElement) === null || _this$_visibleVideoEl === void 0 ? void 0 : _this$_visibleVideoEl.readyState) >= READY_STATE.HAVE_ENOUGH_DATA && this.state === 'ACTIVE' && this.viewportPosition === 'IN-VIEWPORT';
  }
  _shouldPreload() {
    return this.viewportPosition === 'IN-VIEWPORT';
  }
  _preload() {
    var _this$_visibleVideoEl2;
    if (((_this$_visibleVideoEl2 = this._visibleVideoElement) === null || _this$_visibleVideoEl2 === void 0 ? void 0 : _this$_visibleVideoEl2.readyState) < READY_STATE.HAVE_ENOUGH_DATA) {
      var _this$_visibleVideoEl3;
      (_this$_visibleVideoEl3 = this._visibleVideoElement) === null || _this$_visibleVideoEl3 === void 0 || _this$_visibleVideoEl3.load();
    }
  }
  _playVideo() {
    dispatchEvent(this, 'dh-basis-autoplay-attempted');
    var reducedMotion = testReducedMotion();
    var videoElement = this._visibleVideoElement;
    if (videoElement.paused && !reducedMotion && !this.autoplayBlocked) {
      var playPromise = videoElement.play() || Promise.resolve();
      playPromise.catch(e => {
        console.error(e);
        this.autoplayBlocked = true;
      });
    }
  }
  _pauseVideo() {
    var videoElement = this._visibleVideoElement;
    if (videoElement && !videoElement.paused) {
      videoElement.pause();
    }
  }
  get videoElements() {
    return toArray(this.querySelectorAll('video'));
  }
  get disableForcedAttributes() {
    return this.hasAttribute(DISABLE_FORCED_ATTRIBUTES_ATTR);
  }
  get state() {
    return this.getAttribute(STATE_ATTR);
  }
  set state(val) {
    this.setAttribute(STATE_ATTR, val);
  }
  get isPaused() {
    return this.getAttribute(IS_PAUSED);
  }
  set isPaused(val) {
    if (val) {
      this.setAttribute(IS_PAUSED, '');
    } else {
      this.removeAttribute(IS_PAUSED);
    }
  }
  get autoplayBlocked() {
    return this.hasAttribute(AUTOPLAY_BLOCKED) || false;
  }
  set autoplayBlocked(val) {
    toggleAttribute(this, AUTOPLAY_BLOCKED, val);
  }
  get viewportPosition() {
    return this.getAttribute(VIEWPORT_POSITION_ATTR);
  }
  set viewportPosition(val) {
    this.setAttribute(VIEWPORT_POSITION_ATTR, val);
  }
}

export { LevendeStillbilde as default };
//# sourceMappingURL=index.esm.js.map
